<template>
  <div class="component-upload-image">
    <!-- WebP 配置选项 -->
    <div class="webp-config-panel" v-if="showWebpConfig">
      <el-row :gutter="10">
        <el-col :span="12">
          <div class="config-item">
            <span class="config-label">WebP转换:</span>
            <el-switch
              v-model="internalConvertToWebP"
              active-text="启用"
              inactive-text="禁用"
              size="small"
            ></el-switch>
          </div>
        </el-col>
        <el-col :span="12">
          <div class="config-item">
            <span class="config-label">质量:</span>
            <el-select
              v-model="internalWebpQuality"
              :disabled="!internalConvertToWebP"
              size="small"
              placeholder="选择质量"
              style="width: 100%"
            >
              <el-option label="高质量 (专业摄影)" value="high"></el-option>
              <el-option label="中等质量 (推荐)" value="medium"></el-option>
              <el-option label="低质量 (缩略图)" value="low"></el-option>
              <el-option label="最小体积 (图标)" value="smallest"></el-option>
              <el-option label="极限压缩" value="extreme"></el-option>
            </el-select>
          </div>
        </el-col>
      </el-row>
      <div class="config-tip">
        <i class="el-icon-info"></i>
        {{ internalConvertToWebP ? `将转换为WebP格式 (${getQualityLabel(internalWebpQuality)})` : '保持原格式上传' }}
      </div>
    </div>

    <el-upload
      multiple
      :action="uploadImgUrl"
      list-type="picture-card"
      :on-success="handleUploadSuccess"
      :before-upload="handleBeforeUpload"
      :on-progress="handleUploadProgress"
      :limit="limit"
      :on-error="handleUploadError"
      :on-exceed="handleExceed"
      ref="imageUpload"
      :on-remove="handleDelete"
      :show-file-list="true"
      :headers="headers"
      :data="uploadData"
      :file-list="fileList"
      :on-preview="handlePictureCardPreview"
      :class="{hide: this.fileList.length >= this.limit}"
    >
      <i class="el-icon-plus"></i>
    </el-upload>

    <!-- 上传进度 -->
    <el-progress
      v-if="showProgress && uploadProgress > 0 && uploadProgress < 100"
      :percentage="uploadProgress"
      :stroke-width="6"
      style="margin-top: 10px;"
    ></el-progress>

    <!-- 上传提示 -->
    <div class="el-upload__tip" slot="tip" v-if="showTip">
      请上传
      <template v-if="fileSize"> 大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b> </template>
      <template v-if="fileType"> 格式为 <b style="color: #f56c6c">{{ fileType.join("/") }}</b> </template>
      的文件
      <template v-if="!showWebpConfig && convertToWebP">
        <b style="color: #67c23a">（将自动转换为WebP格式 - {{ webpQuality }}质量）</b>
      </template>
    </div>

    <el-dialog
      :visible.sync="dialogVisible"
      title="预览"
      width="800"
      append-to-body
    >
      <img
        :src="dialogImageUrl"
        style="display: block; max-width: 100%; margin: 0 auto"
      />
    </el-dialog>
  </div>
</template>

<script>
import { getToken } from "@/utils/auth";
import { listByIds, delOss } from "@/api/system/oss";
import { getMinioBucket } from "@/api/system/config";
import Cookies from "js-cookie";

export default {
  props: {
    value: [String, Object, Array],
    // 图片数量限制
    limit: {
      type: Number,
      default: 5,
    },
    // 大小限制(MB)
    fileSize: {
       type: Number,
      default: 5,
    },
    // 文件类型, 例如['png', 'jpg', 'jpeg']
    fileType: {
      type: Array,
      default: () => ["png", "jpg", "jpeg","gif","bmp"],
    },
    // 是否显示提示
    isShowTip: {
      type: Boolean,
      default: true
    },
    // 是否转换为WebP
    convertToWebP: {
      type: Boolean,
      default: true
    },
    // WebP质量: high/medium/low/smallest/extreme
    webpQuality: {
      type: String,
      default: 'smallest',
      validator: (value) => ['high', 'medium', 'low', 'smallest', 'extreme'].includes(value)
    },
    // 存储桶名称（如果传入则使用传入值，否则从接口获取）
    bucket: {
      type: String,
      default: ''
    },
    // 是否显示上传进度
    showProgress: {
      type: Boolean,
      default: true
    },
    // 是否显示 WebP 配置面板
    showWebpConfig: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      number: 0,
      uploadList: [],
      uploadProgress: 0,
      dialogImageUrl: "",
      dialogVisible: false,
      hideUpload: false,
      baseUrl: process.env.VUE_APP_BASE_API,
      uploadImgUrl: "https://test-minio.asdf.live/api/upload", // 新的上传接口地址
      headers: {
        Token: Cookies.get('token'),
        Authorization: "Bearer " + getToken(),
      },
      fileList: [],
      // 内部配置状态
      internalConvertToWebP: this.convertToWebP,
      internalWebpQuality: this.webpQuality,
      // 动态获取的 bucket 值
      currentBucket: ''
    };
  },
  mounted() {
    // 如果没有传入 bucket，则从接口获取
    if (!this.bucket) {
      this.fetchBucket();
    } else {
      this.currentBucket = this.bucket;
    }
  },
  computed: {
    // 上传时附加的数据
    uploadData() {
      const data = {};
      // 使用内部配置或外部传入的配置
      const useConvert = this.showWebpConfig ? this.internalConvertToWebP : this.convertToWebP;
      const useQuality = this.showWebpConfig ? this.internalWebpQuality : this.webpQuality;

      if (useConvert) {
        data.convertToWebP = 'true';
        data.webpQuality = useQuality;
      }
      // 使用动态获取的 bucket 值
      if (this.currentBucket) {
        data.bucket = this.currentBucket;
      }
      return data;
    },
    // 是否显示提示
    showTip() {
      return this.isShowTip && (this.fileType || this.fileSize);
    },
  },
  watch: {
    value: {
      async handler(val) {
        if (val) {
          // 首先将值转为数组
          let list;
          if (Array.isArray(val)) {
            list = val;
          } else if (typeof val === 'string') {
            if (val.includes('http') || val.includes('/')) {
              list = val.split(',').map((url, index) => ({
                url: url.trim(),
                name: `image_${index}`,
                ossId: null
              }));
            } else {
              await listByIds(val).then(res => {
                list = res.data;
              })
            }
          }
          this.fileList = list.map((item, index) => {
            if (typeof item === 'string') {
              return { name: `image_${index}`, url: item, ossId: null };
            } else {
              return { name: item.ossId || `image_${index}`, url: item.url, ossId: item.ossId };
            }
          });
        } else {
          this.fileList = [];
          return [];
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    // 获取 bucket 配置
    async fetchBucket() {
      try {
        const response = await getMinioBucket();
        if (response && response.data) {
          this.currentBucket = response.data;
        }
      } catch (error) {
        console.error('获取 bucket 配置失败:', error);
        // 如果获取失败，使用默认值
        this.currentBucket = 'kanbei';
      }
    },
    // 获取质量标签
    getQualityLabel(quality) {
      const labels = {
        high: '高质量',
        medium: '中等质量',
        low: '低质量',
        smallest: '最小体积',
        extreme: '极限压缩'
      };
      return labels[quality] || quality;
    },
    // 上传前loading加载
    handleBeforeUpload(file) {
      let isImg = false;
      if (this.fileType.length) {
        let fileExtension = "";
        if (file.name.lastIndexOf(".") > -1) {
          fileExtension = file.name.slice(file.name.lastIndexOf(".") + 1);
        }
        isImg = this.fileType.some((type) => {
          if (file.type.indexOf(type) > -1) return true;
          if (fileExtension && fileExtension.indexOf(type) > -1) return true;
          return false;
        });
      } else {
        isImg = file.type.indexOf("image") > -1;
      }

      if (!isImg) {
        this.$modal.msgError(`文件格式不正确, 请上传${this.fileType.join("/")}图片格式文件!`);
        return false;
      }
      if (this.fileSize) {
        const isLt = file.size / 1024 / 1024 < this.fileSize;
        if (!isLt) {
          this.$modal.msgError(`上传头像图片大小不能超过 ${this.fileSize} MB!`);
          return false;
        }
      }
      this.$modal.loading("正在上传图片，请稍候...");
      this.uploadProgress = 0;
      this.number++;
    },
    // 文件个数超出
    handleExceed() {
      this.$modal.msgError(`上传文件数量不能超过 ${this.limit} 个!`);
    },
    // 上传进度
    handleUploadProgress(event, file, fileList) {
      this.uploadProgress = Math.round((event.loaded / event.total) * 100);
    },
    // 上传成功回调（新接口格式）
    handleUploadSuccess(res, file) {
      console.log(res, file, 'imginfo')
      if (res.success) {
        // 新接口返回的数据结构
        this.uploadList.push({
          name: res.data.originalName,
          url: res.data.url,
          ossId: res.data.etag,
          cdnUrl: res.data.cdnUrl
        });
        this.uploadedSuccessfully();
      } else {
        this.number--;
        this.$modal.closeLoading();
        this.$modal.msgError(res.error ? res.error.message : '上传失败');
        this.$refs.imageUpload.handleRemove(file);
        this.uploadedSuccessfully();
      }
      this.uploadProgress = 0;
    },
    // 删除图片
    handleDelete(file) {
      const findex = this.fileList.map(f => f.name).indexOf(file.name);
      if(findex > -1) {
        let ossId = this.fileList[findex].ossId;
        if (ossId) {
          delOss(ossId);
        }
        this.fileList.splice(findex, 1);
        this.$emit("input", this.listToString(this.fileList));
      }
    },
    // 上传失败
    handleUploadError(res) {
      this.$modal.msgError("上传图片失败，请重试");
      this.$modal.closeLoading();
      this.uploadProgress = 0;
    },
    // 上传结束处理
    uploadedSuccessfully() {
      if (this.number > 0 && this.uploadList.length === this.number) {
        this.fileList = this.fileList.concat(this.uploadList);
        this.uploadList = [];
        this.number = 0;
        this.$emit("input", this.listToString(this.fileList));
        this.$modal.closeLoading();
      }
    },
    // 预览
    handlePictureCardPreview(file) {
      this.dialogImageUrl = file.url;
      this.dialogVisible = true;
    },
    // 对象转成指定字符串分隔
    listToString(list, separator) {
      let strs = "";
      separator = separator || ",";
      for (let i in list) {
        if (list[i].url) {
          strs += list[i].url + separator;
        }
      }
      return strs != "" ? strs.substr(0, strs.length - 1) : "";
    }
  }
};
</script>
<style scoped lang="scss">
// WebP 配置面板
.webp-config-panel {
  background: #f5f7fa;
  padding: 12px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  border: 1px solid #e4e7ed;

  .config-item {
    display: flex;
    align-items: center;
    gap: 10px;

    .config-label {
      font-size: 14px;
      color: #606266;
      font-weight: 500;
      min-width: 80px;
    }
  }

  .config-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 5px;

    i {
      font-size: 14px;
    }
  }
}

// .el-upload--picture-card 控制加号部分
::v-deep.hide .el-upload--picture-card {
    display: none;
}
// 去掉动画效果
::v-deep .el-list-enter-active,
::v-deep .el-list-leave-active {
    transition: all 0s;
}

::v-deep .el-list-enter, .el-list-leave-active {
  opacity: 0;
  transform: translateY(0);
}
</style>
